import { LogOutIcon, PaletteIcon, ShieldIcon, UserPenIcon, UserRoundIcon } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";

import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import {
  accountInitials,
  displayName,
  displaySubject,
  useAccountProfile,
  type WorkspaceAccount,
} from "../../state/accountProfile";
import { avatarGradient } from "../../lib/avatarGradient";
import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import {
  QUICK_MENU_POPUP_CLASS,
  QUICK_MENU_VIEWPORT_CLASS,
  QuickMenuRow,
  QuickMenuSeparator,
} from "../ui/quick-menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { performSignOut, SignOutTimeoutError } from "./signOut";
import { toastManager } from "../ui/toast";

function backendOrigin(): string {
  return new URL(resolvePrimaryEnvironmentHttpUrl("/")).origin;
}

function AccountAvatar({ account, sizeClass }: { account: WorkspaceAccount; sizeClass: string }) {
  return account.profilePicture ? (
    <img
      alt=""
      className={`${sizeClass} rounded-full object-cover`}
      referrerPolicy="no-referrer"
      src={account.profilePicture}
    />
  ) : (
    <span
      // No picture: a gradient keyed to the person, so the avatar stands off
      // the chrome behind it and stays the same everywhere they sign in.
      className={`${sizeClass} flex items-center justify-center rounded-full font-semibold text-[11px] text-white`}
      style={{ background: avatarGradient(account.email ?? accountInitials(account)) }}
    >
      {accountInitials(account)}
    </span>
  );
}

/**
 * The workspace account chip in the settings sidebar footer — the fork's
 * counterpart to the T3 Connect avatar. Shows the signed-in environment
 * session (profile picture and name from the platform user when the session
 * came through WorkOS) and signs out by revoking the current browser session
 * against the backend, then returning to the login surface.
 *
 * Browser cookie sessions only: the desktop app manages its own trust and
 * never needs a sign-out.
 */
/**
 * @param placement Where the trigger sits: at the foot of the rail (popover
 * opens upward, tooltip to the right) or in the strip above the inset, to the
 * right of Ask AI (popover opens downward, aligned to its right edge).
 */
export function WorkspaceUserMenu({ placement = "rail" }: { placement?: "rail" | "strip" }) {
  const inStrip = placement === "strip";
  const { account } = useAccountProfile();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [open, setOpen] = useState(false);
  const activeOrgId = useActiveOrgId();
  const orgSegment = activeOrgId ? orgUrlSegment(activeOrgId) : null;

  if (!account) {
    return null;
  }

  const signOut = async () => {
    setIsSigningOut(true);
    try {
      await performSignOut({ backendOrigin: backendOrigin() });
    } catch (error) {
      // Only a timeout leaves the person here (anything else has already
      // redirected): unlock the menu so they can try again.
      if (error instanceof SignOutTimeoutError) {
        setIsSigningOut(false);
        toastManager.add({
          type: "error",
          title: "Sign-out not completed",
          description: error.message,
        });
        return;
      }
      throw error;
    }
  };

  const name = displayName(account);
  const settings = (section: "account" | "your-context" | "security" | "appearance") =>
    void navigate(
      (orgSegment
        ? { to: "/$org/settings/$section", params: { org: orgSegment, section } }
        : { to: `/settings/${section}` }) as never,
    );

  // The same popover as the help menu above it: one look for the rail's foot.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              aria-label="Workspace account"
              data-active={open || undefined}
              className={cn(
                "relative z-[1] cursor-pointer rounded-lg p-1 outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                inStrip
                  ? "pointer-events-auto mr-1.5 shrink-0 rounded-full p-0.5 hover:bg-foreground/[0.07] data-[active]:bg-foreground/[0.09] [-webkit-app-region:no-drag]"
                  : "hover:bg-sidebar-row-hover data-[active]:bg-sidebar-row-hover",
              )}
            />
          }
        >
          <AccountAvatar account={account} sizeClass={inStrip ? "size-6" : "size-7"} />
        </TooltipTrigger>
        <TooltipPopup side={inStrip ? "bottom" : "right"}>{name ?? "Account"}</TooltipPopup>
      </Tooltip>
      <PopoverPopup
        side={inStrip ? "bottom" : "top"}
        align={inStrip ? "end" : "start"}
        sideOffset={8}
        aria-label="Account"
        className={QUICK_MENU_POPUP_CLASS}
        viewportClassName={QUICK_MENU_VIEWPORT_CLASS}
      >
        <div className="quick-menu-row flex items-center gap-2.5 px-2.5 py-2">
          <AccountAvatar account={account} sizeClass="size-8" />
          <span className="min-w-0">
            {name ? <span className="block truncate text-[13px] font-medium">{name}</span> : null}
            <span className="block truncate text-xs text-muted-foreground">
              {account.email ?? displaySubject(account.subject)}
            </span>
          </span>
        </div>
        <QuickMenuSeparator />
        <QuickMenuRow
          icon={<UserRoundIcon aria-hidden />}
          onClick={() => {
            setOpen(false);
            settings("account");
          }}
        >
          Account settings
        </QuickMenuRow>
        <QuickMenuRow
          icon={<UserPenIcon aria-hidden />}
          onClick={() => {
            setOpen(false);
            settings("your-context");
          }}
        >
          Your context
        </QuickMenuRow>
        <QuickMenuRow
          icon={<PaletteIcon aria-hidden />}
          onClick={() => {
            setOpen(false);
            settings("appearance");
          }}
        >
          Appearance
        </QuickMenuRow>
        <QuickMenuRow
          icon={<ShieldIcon aria-hidden />}
          onClick={() => {
            setOpen(false);
            settings("security");
          }}
        >
          Security
        </QuickMenuRow>
        <QuickMenuSeparator />
        <QuickMenuRow
          icon={<LogOutIcon aria-hidden />}
          disabled={isSigningOut}
          onClick={() => void signOut()}
        >
          {isSigningOut ? "Signing out…" : "Sign out"}
        </QuickMenuRow>
      </PopoverPopup>
    </Popover>
  );
}
